import React, { useRef } from 'react';
import { graphql } from 'gatsby';
import { navigate } from '@reach/router';

import { Tab } from '@headlessui/react';
import Seo from '@laradevitt/gatsby-theme-just-basics/src/components/seo';
import { defaultValues as contactDefaultValues } from '../components/Forms/Shared/ContactInformation';
import { defaultValues as projectDefaultValues } from '../components/Forms/RequestAQuote/ProjectDetails';
import { FeatureCTASimple } from '../components/Feature';

import { RequestAQuoteController } from '../components/Forms/RequestAQuote/';

import {
  PageHeaderWrapper,
  PageHeaderHeading,
  Section,
  SectionHeader,
  SectionHeaderHeading,
  SectionWrapper,
} from '../components/Layout/';

import { Center } from '../components/EveryLayout';

import { slugToVariableName } from '../helpers';

import {
  useAllMenuItem,
  useAllMenuItemTopServicesProducts
} from '../hooks';

const TabWrapper = ({ children, ...rest }) => {
  const selected = rest.selected;
  return (
    <Tab
      className={({ selected }) =>
        `w-full md:w-auto px-4 pt-3 pb-4 text-[1rem] uppercase tracking-wide md:rounded-t-xl focus-visible:outline-none ${
          selected
            ? 'bg-dark-gray-500 text-white'
            : 'transition duration-hover text-color-base bg-light-gray-200 hover:bg-light-gray-50'
        }`
      }
      {...rest}
    >
      {children}
    </Tab>
  )
};

const TabPanelWrapper = ({ children }) => (
  <Tab.Panel>
    <div>
      <div className="max-w-7xl mx-auto bg-white shadow-lg pb-8">
        <Section>
          <Center>
            {children}
          </Center>
        </Section>
      </div>
    </div>
  </Tab.Panel>
);

/* eslint-disable react/prop-types */
export const Head = ({ data: { page }}) => {

  const seoImageUrl = page.data.image?.thumbnails?.OpenGraph?.url || page.data.image?.thumbnails?.StandardLandscape?.url || page.data.image?.url;

  return (
    <Seo
      title={page.data?.title?.text || ''}
      description={page.data.meta_description || page.data.summary || ''}
      path={`/${page.uid}/`}
      image={seoImageUrl}
    />
  );
};
/* eslint-enable react/prop-types */

const PageRequestAQuote = ({ location, data }) => {
  const { page } = data;

  // Get all top level menu items for service/product categories. We are using
  // the same structure for quote request form categories.
  const topMenuItems = useAllMenuItemTopServicesProducts();
  const allMenuItems = useAllMenuItem();

  // Create an array consisting of menu item uids. This will be used to create
  // tabs for the quote request form.
  const tabs = topMenuItems.map(function({ node }) {
    return node.uid;
  });

  // select the appropriate tab
  const urlEls = location.hash.slice(1).split('/').filter((el) => el);
  const element = urlEls[0];
  const index = tabs.indexOf(element);
  const selectedIndex = index > -1 ? index : 0;

  // set default contact info values on a ref so we can persist them across tabs
  // const contactValues = useRef(contactDefaultValues);

  // TODO: refactor nameing
  // combine default values for contact info and project details
  const contactValues = useRef({
    ...contactDefaultValues,
    ...projectDefaultValues
  });

  // helper for updating contactValues ref
  const setContactValues = (v) => {
    contactValues.current = v;
  }

  return (
    <>
      <PageHeaderWrapper>
        <PageHeaderHeading>{page.data?.title?.text || ''}</PageHeaderHeading>
      </PageHeaderWrapper>
      <SectionWrapper className="bg-light-gray-100 py-16 !mt-0">
        <SectionHeader>
          <SectionHeaderHeading className="text-2xl text-center">
            What would you like us to quote on today?
          </SectionHeaderHeading>
        </SectionHeader>
        <Tab.Group selectedIndex={selectedIndex} onChange={(index) => {
          // note that navigate() (not the onChange event) will fire location change twice
          // this only happens in Tab.Group. Link or navigate() in an onClick event will only fire once
          navigate(`#${tabs[index]}`);
        }}>
          <div className="max-w-[76rem] mx-auto">
            <Tab.List className="p-2 pb-0 flex flex-wrap justify-center items-center gap-2 lg:gap-1 2xl:bg-light-gray-200 2xl:rounded-t-2xl">
              {topMenuItems.map(function({ node }) {
                return (
                  <TabWrapper key={`tab-${node.uid}`} selected>
                    <div>
                      {node.label}
                    </div>
                  </TabWrapper>
                );
              })}
            </Tab.List>
          </div>
          <Tab.Panels>
            {topMenuItems.map(function({ node }) {
              const varName = slugToVariableName(node.uid);
              const childNodes = allMenuItems[varName] || [];
              return (
                <TabPanelWrapper key={`panel-${node.uid}`}>
                  <RequestAQuoteController
                    childCategories={childNodes}
                    contactValues={contactValues}
                    location={location}
                    setContactValues={setContactValues}
                    topCategoryItem={node}
                    />
                </TabPanelWrapper>
              );
            })}
          </Tab.Panels>
        </Tab.Group>
      </SectionWrapper>
      <Section>
        <FeatureCTASimple />
      </Section>
    </>
  );
};

export const pageQuery = graphql`
  query PageRequestAQuoteQuery {
    site {
      ...SiteFragment
    }
    page: prismicPage(
      prismicId: {
        eq: "ZL_y8BIAACMA8GKu"
      }
    ) {
      ...PrismicPageFragment
    }
  }
`;

export default PageRequestAQuote;
